import React from 'react';
import { Form, Formik, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';

import { ConnectFormikToStepper } from 'hellospa/components/prep-and-send/formik-utils';

import FolderPicker from '../folder-picker';
import Filename from './filename';
import { PageHeader } from 'hellospa/components/prep-and-send/header';

import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as deepIntegrationActions from 'hellospa/page/prep-and-send/data/actions/deep-integration';
import * as types from 'hellospa/page/prep-and-send/data/types';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import { DeepIntegrationQuotaValidation } from 'hellospa/components/deep-integration/upsell';

import messages from './intl';
import styles from './index.scss';
import { Dispatch } from 'hellospa/redux/types';

type OnSubmitTypes = {
  filename: string,
  folderPath: string,
}

// removes an extension from a filename
const trimFileName = (filename: string) => {
  if (filename.includes('.')) {
    return filename.replace(/.[^.]+$/, '');
  }

  return filename;
};

const Dropin: types.WorkflowComponent = ({ formRef, onSaveActions }) => {
  const intl = useIntl();
  const dispatch = useDispatch<Dispatch>();
  const initialFolderSelection = useSelector(selectors.getExternalFolderPath);

  const guid = useSelector(selectors.getTransmissionGroupGuid);
  const document = useSelector(selectors.getDocument);
  const workflow = useSelector(selectors.getWorkflow);
  const defaultSavePath = useSelector(selectors.getExternalDefaultSavePath);
  const { lockDocumentTitle } = useSelector(selectors.getFlags);

  const getNewFolderPath = (path: string | undefined) => {
    if (path === undefined) {
      return '';
    }
    return path === '' ? '/' : path;
  };

  const onSubmit = async ({ filename, folderPath }: OnSubmitTypes) => {
    const body = {
      filename,
      folderPath,
      guid,
    };

    const success = await dispatch(deepIntegrationActions.saveDropboxPath(body));

    if (success) {
      if (!document.title && !lockDocumentTitle) {
        await dispatch(actions.setDocument({
          title: filename,
        }));
      }
      await dispatch(deepIntegrationActions.setFilename(filename));
      if (workflow.tags.includes(types.WorkflowTags.DeepIntegrationSelfSign)) {
        await dispatch(actions.workflowOnSave(onSaveActions));
      }
    }
  };

  const schema = selectors.getDropinSchema;
  const currentFile = useSelector(selectors.getCurrentFile);
  const filename = currentFile ? trimFileName(currentFile.name) : '';

  const getInitialFolderSelection = () => {
    return defaultSavePath || initialFolderSelection;
  };

  const initialValues = {
    filename,
    folderPath: getInitialFolderSelection(),
  };

  return (
    <div className={styles.container}>
      <PageHeader
        dataQaRef="page-header"
        text={intl.formatMessage(messages.header)} />
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validateOnChange={false}
        validationSchema={schema}
        innerRef={types.useFormikRef(formRef)}
        component={({ values, setFieldValue, errors }) => {
          const onFolderSelected = (path: string | undefined) => {
            const newFolderPath = getNewFolderPath(path);
            setFieldValue('folderPath', newFolderPath);
          };
          return (<Form>
            <ConnectFormikToStepper />
            <DeepIntegrationQuotaValidation />
            <Filename />
            <Field
              id="folderPath"
              as={FolderPicker}
              defaultSavePath={defaultSavePath}
              name="folderPath"
              type="text"
              data-qa-ref="fields.folderPath"
              rows={1}
              onFolderSelected={onFolderSelected}
              hasError={errors.folderPath}
              initialFolderSelection={values.folderPath} />
          </Form>);
        }
        } />
    </div>
  );
};

Dropin.getText = (workflow, state) => {
  const msg = defineMessages({
    title: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'File location',
    },
    titleTemplate: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Review and save',
    },
    save: {
      id: '',
      description: 'text in save button',
      defaultMessage: 'Save',
    },
  });

  /**
   * We can make the assumption that if there are no recipients,
   * this is the last page in the prep-and-send flow.
   * Therefore change CTA 'Next' to 'Save'
   */
  const recipients = selectors.getRecipients(state);
  if (recipients && !recipients.length) {
    return {
      ...msg,
      next: msg.save,
    };
  }

  return msg;
};

export default Dropin;
